import React from 'react';
import { Link } from 'react-router-dom';
import { routesPaths } from 'constants/routesPaths';
import './MockCreatorFinancePage.scss';

const headlineMetrics = [
  { label: 'GMV this month', value: '$12,480', hint: 'Gross fan payments' },
  { label: 'Net payable', value: '$9,984', hint: 'After platform and reserve holds', accent: true },
  { label: 'Next payout', value: 'Apr 18', hint: 'Estimated $4,120 to ....4829' },
  { label: 'Reserve balance', value: '$640', hint: 'Held for disputes and refunds' }
];

const waterfall = [
  { label: 'Gross revenue', value: '$12,480' },
  { label: 'Platform fee (20%)', value: '-$2,496' },
  { label: 'Stripe fees', value: '-$386' },
  { label: 'Refunds and disputes', value: '-$154' },
  { label: 'Rolling reserve', value: '-$460' },
  { label: 'Net creator payable', value: '$8,984', strong: true }
];

const revenueSources = [
  { label: 'Subscriptions', value: '$5,840', percent: '47%', width: '47%' },
  { label: 'PPV events', value: '$3,610', percent: '29%', width: '29%' },
  { label: 'Live tips', value: '$1,420', percent: '11%', width: '11%' },
  { label: 'Sponsors and ads', value: '$980', percent: '8%', width: '8%' },
  { label: 'Digital products', value: '$630', percent: '5%', width: '5%' }
];

const fanIntelligence = [
  { label: 'Top spender concentration', value: '18%', hint: 'Top 10 fans of monthly revenue' },
  { label: 'Subscriber churn risk', value: '42 accounts', hint: 'Likely to lapse in 14 days' },
  { label: 'Repeat buyer rate', value: '61%', hint: 'Bought more than once in last 90 days' },
  { label: 'VIP segment', value: '126 fans', hint: 'Spent over $250 lifetime' }
];

const liveEvents = [
  {
    name: 'Spring Classic',
    sellThrough: '82%',
    viewers: '2,842',
    revenue: '$4,180',
    status: 'Live'
  },
  {
    name: 'Locker Room AMA',
    sellThrough: '64%',
    viewers: '910',
    revenue: '$860',
    status: 'Scheduled'
  },
  { name: 'Post Match Recap', sellThrough: '48%', viewers: '612', revenue: '$420', status: 'Draft' }
];

const contentPerformance = [
  {
    title: 'Micd Up Finals',
    type: 'Series',
    watchTime: '72%',
    revenue: '$1,420',
    conversion: '14.8%'
  },
  {
    title: 'Training Camp Live',
    type: 'Live replay',
    watchTime: '64%',
    revenue: '$930',
    conversion: '11.2%'
  },
  {
    title: 'Weekly Insider',
    type: 'Podcast',
    watchTime: '51%',
    revenue: '$540',
    conversion: '7.1%'
  }
];

const complianceInbox = [
  { title: 'W-9 on file', detail: 'Verified Apr 2', status: 'Complete' },
  { title: 'Bank account verification', detail: 'No action required', status: 'Ready' },
  { title: 'Identity refresh', detail: 'Due in 24 days', status: 'Monitor' },
  { title: 'Payout reserve notice', detail: 'Rolling reserve 5% active', status: 'Info' }
];

const crmActions = [
  { title: 'Win-back expiring subscribers', detail: '42 accounts ready for offer' },
  { title: 'Invite VIP fans to live Q&A', detail: '126 fans in premium segment' },
  { title: 'Bundle replay plus merch', detail: 'Best fit for Spring Classic buyers' }
];

const transactions = [
  {
    id: 'pi_3Qx',
    date: 'Apr 12, 2026',
    type: 'PPV - Spring Classic',
    gross: '$24.99',
    share: '$20.00',
    status: 'Paid'
  },
  {
    id: 'pi_3Qw',
    date: 'Apr 11, 2026',
    type: 'Subscription renewal',
    gross: '$9.99',
    share: '$8.00',
    status: 'Paid'
  },
  {
    id: 'pi_3Qv',
    date: 'Apr 10, 2026',
    type: 'Tip - live chat',
    gross: '$15.00',
    share: '$12.00',
    status: 'Pending'
  },
  {
    id: 'pi_3Qu',
    date: 'Apr 8, 2026',
    type: 'PPV - Night match',
    gross: '$19.99',
    share: '$16.00',
    status: 'Paid'
  }
];

const supportTimeline = [
  { date: 'Today', event: 'Daily payout forecast generated', owner: 'Automated' },
  { date: 'Apr 13', event: 'Reserve hold reduced after dispute window', owner: 'Finance Ops' },
  { date: 'Apr 11', event: 'Connect capabilities re-verified', owner: 'Compliance' }
];

const forecastMetrics = [
  { label: 'Projected next 30 days', value: '$13.9k' },
  { label: 'Renewal forecast', value: '91%' },
  { label: 'Downside risk', value: '$1.4k' }
];

const fallbackStyles = {
  page: {
    minHeight: '100vh',
    background: '#0c0c0f',
    color: '#e8e8ec',
    fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif'
  },
  topbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    padding: '16px 24px',
    borderBottom: '1px solid #24242c',
    background: '#111116'
  },
  shell: {
    display: 'grid',
    gridTemplateColumns: '220px 1fr'
  },
  nav: {
    padding: '24px 18px',
    borderRight: '1px solid #24242c',
    background: '#111116'
  },
  main: {
    padding: '24px 28px 40px'
  },
  title: {
    color: '#fff',
    fontSize: '32px',
    margin: '0 0 8px'
  },
  subtitle: {
    color: '#a0a0b0',
    maxWidth: '760px',
    marginBottom: '24px'
  },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: '12px',
    marginBottom: '16px'
  },
  card: {
    background: '#16161d',
    border: '1px solid #2a2a34',
    borderRadius: '12px',
    padding: '18px'
  },
  panel: {
    background: '#16161d',
    border: '1px solid #2a2a34',
    borderRadius: '12px',
    padding: '18px',
    marginBottom: '16px'
  },
  tableWrap: {
    background: '#16161d',
    border: '1px solid #2a2a34',
    borderRadius: '12px',
    padding: '8px 18px',
    overflowX: 'auto'
  },
  link: {
    color: '#a78bfa'
  }
};

const MockCreatorFinancePage = () => (
  <div className="mock-program-ui mock-program-ui--creator" style={fallbackStyles.page}>
    <header className="mock-program-ui__topbar" style={fallbackStyles.topbar}>
      <div className="mock-program-ui__brand">
        <Link
          to={routesPaths.home}
          className="mock-program-ui__link mock-program-ui__link--back"
          style={{ ...fallbackStyles.link, marginRight: 14, fontSize: 13 }}
          title="Back to Identifi home"
        >
          &larr; Home
        </Link>
        <Link
          to={routesPaths.home}
          className="mock-program-ui__logo-link"
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <span className="mock-program-ui__logo">Identifi</span>
        </Link>
        <span className="mock-program-ui__badge">Prototype UI - enterprise creator workspace</span>
      </div>
      <div className="mock-program-ui__topbar-right">
        <span className="mock-program-ui__user">Alex Morgan / @alexm</span>
        <Link
          className="mock-program-ui__link"
          style={fallbackStyles.link}
          to={routesPaths.mockOrganizationDashboard}
        >
          Open org mock &rarr;
        </Link>
      </div>
    </header>

    <div className="mock-program-ui__shell" style={fallbackStyles.shell}>
      <aside className="mock-program-ui__nav" style={fallbackStyles.nav}>
        <p className="mock-program-ui__nav-label">Creator</p>
        <button
          type="button"
          className="mock-program-ui__nav-item mock-program-ui__nav-item--active"
        >
          Overview
        </button>
        <button type="button" className="mock-program-ui__nav-item">
          Revenue and payouts
        </button>
        <button type="button" className="mock-program-ui__nav-item">
          Live event center
        </button>
        <button type="button" className="mock-program-ui__nav-item">
          Fans and CRM
        </button>
        <button type="button" className="mock-program-ui__nav-item">
          Compliance
        </button>
        <button type="button" className="mock-program-ui__nav-item">
          Audit trail
        </button>
      </aside>

      <main className="mock-program-ui__main" style={fallbackStyles.main}>
        <div className="mock-program-ui__toolbar">
          <div className="mock-program-ui__toolbar-group">
            <span className="mock-program-ui__toolbar-chip mock-program-ui__toolbar-chip--active">
              Last 30 days
            </span>
            <span className="mock-program-ui__toolbar-chip">All entities</span>
            <span className="mock-program-ui__toolbar-chip">USD</span>
          </div>
          <div className="mock-program-ui__toolbar-group">
            <button type="button" className="mock-program-ui__btn mock-program-ui__btn--ghost">
              Export statement
            </button>
            <button type="button" className="mock-program-ui__btn">
              Create campaign
            </button>
          </div>
        </div>

        <h1 className="mock-program-ui__title" style={fallbackStyles.title}>
          Enterprise creator dashboard
        </h1>
        <p className="mock-program-ui__subtitle" style={fallbackStyles.subtitle}>
          A platform-grade view of cash flow, audience intelligence, live event monetization,
          compliance, and support history.
        </p>

        <div className="mock-program-ui__cards" style={fallbackStyles.cards}>
          {headlineMetrics.map(metric => (
            <div
              key={metric.label}
              className={`mock-program-ui__card${
                metric.accent ? ' mock-program-ui__card--accent' : ''
              }`}
              style={fallbackStyles.card}
            >
              <span className="mock-program-ui__card-label">{metric.label}</span>
              <strong className="mock-program-ui__card-value">{metric.value}</strong>
              <span className="mock-program-ui__card-hint">{metric.hint}</span>
            </div>
          ))}
        </div>

        <div
          className="mock-program-ui__panel mock-program-ui__panel--connect"
          style={fallbackStyles.panel}
        >
          <div>
            <h2 className="mock-program-ui__panel-title">Payout operations and account health</h2>
            <p className="mock-program-ui__panel-text">
              Status:{' '}
              <span className="mock-program-ui__status mock-program-ui__status--ok">
                Payouts enabled
              </span>
              . Daily payouts, 5% reserve active, bank account verified, no blocked capabilities.
            </p>
          </div>
          <div className="mock-program-ui__toolbar-group">
            <button type="button" className="mock-program-ui__btn mock-program-ui__btn--ghost">
              View in Stripe Express
            </button>
            <button type="button" className="mock-program-ui__btn">
              Update payout rules
            </button>
          </div>
        </div>

        <div className="mock-program-ui__section-grid mock-program-ui__section-grid--two">
          <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
            <h2 className="mock-program-ui__panel-title">Net earnings waterfall</h2>
            <div className="mock-program-ui__metric-list">
              {waterfall.map(item => (
                <div
                  key={item.label}
                  className={`mock-program-ui__metric-row${
                    item.strong ? ' mock-program-ui__metric-row--strong' : ''
                  }`}
                >
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          </div>

          <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
            <h2 className="mock-program-ui__panel-title">Revenue by source</h2>
            <div className="mock-program-ui__bar-list">
              {revenueSources.map(source => (
                <div key={source.label} className="mock-program-ui__bar-item">
                  <div className="mock-program-ui__bar-copy">
                    <span>{source.label}</span>
                    <strong>{source.value}</strong>
                  </div>
                  <div className="mock-program-ui__bar-track">
                    <div className="mock-program-ui__bar-fill" style={{ width: source.width }} />
                  </div>
                  <span className="mock-program-ui__bar-percent">{source.percent}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mock-program-ui__section-grid mock-program-ui__section-grid--two">
          <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
            <div className="mock-program-ui__panel-header">
              <h2 className="mock-program-ui__panel-title">Fan intelligence</h2>
              <span className="mock-program-ui__micro-label">CRM ready</span>
            </div>
            <div className="mock-program-ui__mini-grid">
              {fanIntelligence.map(item => (
                <div key={item.label} className="mock-program-ui__mini-stat">
                  <span className="mock-program-ui__mini-label">{item.label}</span>
                  <strong className="mock-program-ui__mini-value">{item.value}</strong>
                  <span className="mock-program-ui__mini-hint">{item.hint}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
            <div className="mock-program-ui__panel-header">
              <h2 className="mock-program-ui__panel-title">Forecasting</h2>
              <span className="mock-program-ui__micro-label">Cash planning</span>
            </div>
            <div className="mock-program-ui__mini-grid">
              {forecastMetrics.map(item => (
                <div key={item.label} className="mock-program-ui__mini-stat">
                  <span className="mock-program-ui__mini-label">{item.label}</span>
                  <strong className="mock-program-ui__mini-value">{item.value}</strong>
                </div>
              ))}
            </div>
            <div className="mock-program-ui__callout">
              Forecast notes: subscription renewals remain stable, live event demand is up 12%, and
              dispute exposure is currently low.
            </div>
          </div>
        </div>

        <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
          <div className="mock-program-ui__panel-header">
            <h2 className="mock-program-ui__panel-title">Live event command center</h2>
            <button type="button" className="mock-program-ui__btn mock-program-ui__btn--ghost">
              Create event
            </button>
          </div>
          <div className="mock-program-ui__table-wrap" style={fallbackStyles.tableWrap}>
            <table className="mock-program-ui__table">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Sell-through</th>
                  <th>Viewers</th>
                  <th>Revenue</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {liveEvents.map(event => (
                  <tr key={event.name}>
                    <td>{event.name}</td>
                    <td>{event.sellThrough}</td>
                    <td>{event.viewers}</td>
                    <td>{event.revenue}</td>
                    <td>
                      <span
                        className={`mock-program-ui__pill ${
                          // eslint-disable-next-line no-nested-ternary
                          event.status === 'Live'
                            ? 'mock-program-ui__pill--ok'
                            : event.status === 'Scheduled'
                            ? 'mock-program-ui__pill--pending'
                            : 'mock-program-ui__pill--warn'
                        }`}
                      >
                        {event.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
          <h2 className="mock-program-ui__panel-title">Content performance</h2>
          <div className="mock-program-ui__table-wrap" style={fallbackStyles.tableWrap}>
            <table className="mock-program-ui__table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Watch time</th>
                  <th>Revenue</th>
                  <th>Conversion</th>
                </tr>
              </thead>
              <tbody>
                {contentPerformance.map(item => (
                  <tr key={item.title}>
                    <td>{item.title}</td>
                    <td>{item.type}</td>
                    <td>{item.watchTime}</td>
                    <td>{item.revenue}</td>
                    <td>{item.conversion}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mock-program-ui__section-grid mock-program-ui__section-grid--two">
          <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
            <h2 className="mock-program-ui__panel-title">Compliance inbox</h2>
            <div className="mock-program-ui__checklist">
              {complianceInbox.map(item => (
                <div key={item.title} className="mock-program-ui__check-item">
                  <div>
                    <strong>{item.title}</strong>
                    <span>{item.detail}</span>
                  </div>
                  <span className="mock-program-ui__micro-label">{item.status}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
            <h2 className="mock-program-ui__panel-title">CRM actions</h2>
            <div className="mock-program-ui__checklist">
              {crmActions.map(item => (
                <div key={item.title} className="mock-program-ui__check-item">
                  <div>
                    <strong>{item.title}</strong>
                    <span>{item.detail}</span>
                  </div>
                  <button
                    type="button"
                    className="mock-program-ui__btn mock-program-ui__btn--ghost"
                  >
                    Run
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
          <h2 className="mock-program-ui__panel-title">Transaction ledger</h2>
          <div className="mock-program-ui__table-wrap" style={fallbackStyles.tableWrap}>
            <table className="mock-program-ui__table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Gross</th>
                  <th>Your share</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(row => (
                  <tr key={row.id}>
                    <td>{row.date}</td>
                    <td>{row.type}</td>
                    <td>{row.gross}</td>
                    <td>{row.share}</td>
                    <td>
                      <span
                        className={
                          row.status === 'Paid'
                            ? 'mock-program-ui__pill mock-program-ui__pill--ok'
                            : 'mock-program-ui__pill mock-program-ui__pill--pending'
                        }
                      >
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
          <h2 className="mock-program-ui__panel-title">Support and audit trail</h2>
          <div className="mock-program-ui__timeline">
            {supportTimeline.map(item => (
              <div key={item.date + item.event} className="mock-program-ui__timeline-item">
                <span className="mock-program-ui__timeline-date">{item.date}</span>
                <div>
                  <strong>{item.event}</strong>
                  <span>{item.owner}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  </div>
);

export default MockCreatorFinancePage;
